/**
 * RepositoryCommitControls Component
 * Shows commit, pull, and discard controls for repository sync
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Upload,
  Download,
  RotateCcw,
  ChevronDown,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { cn } from '../../../lib/utils';
import type { DataSourceProvider } from '../models/DataSource';
import { useTranslation } from '@/hooks/useTranslation';

interface RepositoryCommitControlsProps {
  /** Number of uncommitted changes */
  uncommittedCount: number;
  /** Number of new records */
  newRecordCount: number;
  /** Number of conflicts */
  conflictCount: number;
  /** Whether there are any changes to commit */
  hasChanges: boolean;
  /** Whether there are any conflicts blocking commit */
  hasConflicts: boolean;
  /** Whether commit operation is in progress */
  isCommitting: boolean;
  /** Whether pull operation is in progress */
  isPulling: boolean;
  /** Whether discard operation is in progress */
  isDiscarding: boolean;
  /** Callback to commit changes */
  onCommit: () => Promise<void>;
  /** Callback to pull from repository */
  onPull: () => Promise<void>;
  /** Callback to discard all changes */
  onDiscardAll: () => Promise<void>;
  /** Callback to resolve conflicts */
  onResolveConflicts?: () => void;
  /** Whether controls are disabled */
  disabled?: boolean;
  /** Whether commit action is restricted by policy */
  commitRestricted?: boolean;
  /** Additional class name */
  className?: string;
  /** Whether to show the pull button */
  showPull?: boolean;
  /** Connected provider type (used for provider-specific tooltip messaging) */
  provider?: DataSourceProvider;
  /** Reviewed fact lane used for primary-source write-back. */
  sourceTypeLabel?: string;
}

export const RepositoryCommitControls: React.FC<RepositoryCommitControlsProps> = ({
  uncommittedCount,
  newRecordCount,
  conflictCount,
  hasChanges,
  hasConflicts,
  isCommitting,
  isPulling,
  isDiscarding,
  onCommit,
  onPull,
  onDiscardAll,
  onResolveConflicts,
  disabled = false,
  commitRestricted = false,
  className,
  showPull = true,
  provider,
  sourceTypeLabel,
}) => {
  const { t } = useTranslation('diagram');
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const isLoading = isCommitting || isPulling || isDiscarding;

  const handleCommit = useCallback(async () => {
    if (hasConflicts) {
      onResolveConflicts?.();
      return;
    }
    await onCommit();
  }, [hasConflicts, onCommit, onResolveConflicts]);

  const handleDiscard = useCallback(async () => {
    setShowDiscardDialog(false);
    await onDiscardAll();
  }, [onDiscardAll]);

  const modifiedCount = uncommittedCount - newRecordCount - conflictCount;
  const isCommitRestricted = commitRestricted && !hasConflicts;
  const isCommitDisabled =
    disabled || isLoading || (!hasChanges && !hasConflicts) || isCommitRestricted;
  const hasProviderSyncDelay = provider === 'excel_online' || provider === 'bamboohr' || provider === 'azure_ad';
  const commitTooltipPrimary = useMemo(() => {
    if (isCommitRestricted) {
      return t('assistant.repositoryCommitRestrictedTooltip', {
        defaultValue:
          'Only the admin (the person who first authorized this data source) can commit updates.',
      });
    }
    if (hasConflicts) {
      return t('assistant.repositoryCommitTooltipConflicts', {
        defaultValue: 'Resolve conflicts before committing',
      });
    }
    if (hasChanges) {
      return t('assistant.repositoryCommitTooltipHasChanges', {
        count: uncommittedCount,
        defaultValue: `Write back ${uncommittedCount} change${uncommittedCount > 1 ? 's' : ''} to the reviewed primary source`,
      });
    }
    return t('assistant.repositoryCommitTooltipNoChanges', {
      defaultValue: 'No changes to commit',
    });
  }, [isCommitRestricted, hasConflicts, hasChanges, uncommittedCount, t]);

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Uncommitted count badge */}
      {hasChanges && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant={hasConflicts ? 'destructive' : 'secondary'}
                className={cn(
                  'cursor-default',
                  hasConflicts && 'animate-pulse'
                )}
              >
                {hasConflicts && <AlertTriangle className="h-3 w-3 mr-1" />}
                {uncommittedCount}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-xs">
                {newRecordCount > 0 && (
                  <div>
                    {t('assistant.repositoryChangeSummaryNew', {
                      count: newRecordCount,
                      defaultValue: `${newRecordCount} new record${newRecordCount > 1 ? 's' : ''}`,
                    })}
                  </div>
                )}
                {modifiedCount > 0 && (
                  <div>
                    {t('assistant.repositoryChangeSummaryModified', {
                      count: modifiedCount,
                      defaultValue: `${modifiedCount} modified`,
                    })}
                  </div>
                )}
                {conflictCount > 0 && (
                  <div className="text-red-500">
                    {t('assistant.repositoryChangeSummaryConflict', {
                      count: conflictCount,
                      defaultValue: `${conflictCount} conflict${conflictCount > 1 ? 's' : ''}`,
                    })}
                  </div>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Commit button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                variant={hasConflicts ? 'destructive' : 'default'}
                size="sm"
                onClick={handleCommit}
                disabled={isCommitDisabled}
                className="gap-1"
              >
                {hasConflicts ? (
                  <>
                    <AlertTriangle className="h-4 w-4" />
                    {t('assistant.repositoryCommitResolve', { defaultValue: 'Resolve' })}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    {t('assistant.repositoryWriteback', { defaultValue: 'Write back' })}
                  </>
                )}
                {isCommitting && (
                  <Loader2 className="ml-1 h-4 w-4 animate-spin" />
                )}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-xs space-y-1">
              <div>{commitTooltipPrimary}</div>
              {sourceTypeLabel && <div>Fact lane: {sourceTypeLabel}</div>}
              {hasProviderSyncDelay && (
                <div>
                  {t('assistant.repositoryCommitTooltipSyncDelay', {
                    defaultValue:
                      'There will be a 5-15 minutes delay in data sync between the provider and the Creately app.',
                  })}
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Pull button */}
      {showPull && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={onPull}
                disabled={disabled || isLoading}
                className="gap-1"
              >
                <Download className="h-4 w-4" />
                {t('assistant.repositoryPull', { defaultValue: 'Pull' })}
                {isPulling && (
                  <Loader2 className="ml-1 h-4 w-4 animate-spin" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {t('assistant.repositoryPullTooltip', {
                defaultValue: 'Pull latest from repository',
              })}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* More options dropdown */}
      {hasChanges && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              disabled={disabled || isLoading}
              className="h-8 w-8 p-0"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => setShowDiscardDialog(true)}
              className="text-destructive focus:text-destructive"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              {t('assistant.repositoryDiscardAllMenuItem', { defaultValue: 'Discard all changes' })}
            </DropdownMenuItem>
            {hasConflicts && onResolveConflicts && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onResolveConflicts}>
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  {t('assistant.repositoryResolveConflictsMenuItem', {
                    count: conflictCount,
                    defaultValue: `Resolve conflicts (${conflictCount})`,
                  })}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Discard confirmation dialog */}
      <Dialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('assistant.repositoryDiscardDialogTitle', { defaultValue: 'Discard all changes?' })}
            </DialogTitle>
            <DialogDescription>
              {t('assistant.repositoryDiscardDialogBody', {
                count: uncommittedCount,
                defaultValue: `This will discard ${uncommittedCount} uncommitted change${
                  uncommittedCount > 1 ? 's' : ''
                }.`,
              })}
              {newRecordCount > 0 && (
                <span className="block mt-2 text-destructive">
                  {t('assistant.repositoryDiscardDialogNewRecords', {
                    count: newRecordCount,
                    defaultValue: `${newRecordCount} new record${
                      newRecordCount > 1 ? 's' : ''
                    } will be deleted from the diagram.`,
                  })}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDiscardDialog(false)}
            >
              {t('assistant.mapOrgChartFieldsCancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDiscard}
              disabled={isDiscarding}
            >
              {isDiscarding
                ? t('assistant.repositoryDiscardDialogConfirming', { defaultValue: 'Discarding...' })
                : t('assistant.repositoryDiscardDialogConfirm', { defaultValue: 'Discard' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/**
 * Compact version for toolbar use
 */
interface CompactCommitControlsProps {
  uncommittedCount: number;
  hasConflicts: boolean;
  isLoading: boolean;
  onCommit: () => void;
  onPull: () => void;
  disabled?: boolean;
}

export const CompactCommitControls: React.FC<CompactCommitControlsProps> = ({
  uncommittedCount,
  hasConflicts,
  isLoading,
  onCommit,
  onPull,
  disabled = false,
}) => {
  const { t } = useTranslation('diagram');
  const compactCommitTooltip = hasConflicts
    ? t('assistant.repositoryCompactCommitTooltipConflicts', { defaultValue: 'Resolve conflicts' })
    : uncommittedCount > 0
      ? t('assistant.repositoryCompactCommitTooltipHasChanges', {
          count: uncommittedCount,
          defaultValue: `Commit ${uncommittedCount} changes`,
        })
      : t('assistant.repositoryCompactCommitTooltipNoChanges', { defaultValue: 'No changes' });

  return (
    <div className="flex items-center gap-1">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={hasConflicts ? 'destructive' : uncommittedCount > 0 ? 'default' : 'ghost'}
              size="icon"
              className="h-8 w-8"
              onClick={onCommit}
              disabled={disabled || isLoading || uncommittedCount === 0}
            >
              {uncommittedCount > 0 ? (
                <div className="relative">
                  <Upload className="h-4 w-4" />
                  <span className="absolute -top-1 -right-1 h-3 w-3 text-[8px] bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                    {uncommittedCount > 9 ? '9+' : uncommittedCount}
                  </span>
                </div>
              ) : (
                <Upload className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{compactCommitTooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onPull}
              disabled={disabled || isLoading}
            >
              <Download className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t('assistant.repositoryCompactPullTooltip', { defaultValue: 'Pull from repository' })}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
